// hooks
import { type MutationParams, Pagination, useCommonMutation, useCommonQuery } from 'hooks';

// modules
import type { BaseApiResponse, CommonQueryOptions, ResultError } from 'modules';

// apis
import { AuthAPI } from '../apis/auth.api';

// keys
import { authKeys } from '../keys/auth.key';

import type {
  ReqApproval,
  ReqApprovalList,
  ReqChangeId,
  ReqLogin,
  ReqReject,
  ReqSignUp,
  ResLogin,
  ResSignUp,
  UserApprovalInfo
} from '../models/auth.model';

// libraries
import { type UseMutationResult } from '@tanstack/react-query';

export const authApi = new AuthAPI();

/** 회원가입: 사용자 정보(파일 X) 쿼리 훅 */
export const usePostSignUp = (): UseMutationResult<
  BaseApiResponse<ResSignUp>,
  ResultError,
  MutationParams<BaseApiResponse<ResSignUp>, ReqSignUp>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<ResSignUp>, ResultError, ReqSignUp>({
    mutationKey: authKeys.SIGN_UP(),
    mutationFn: async (data) => await authApi.signUp(data),
    queryKeyToInvalidate: authKeys.SIGN_UP()
  });
};

/** 회원가입: 사용자 정보 + 파일 쿼이 훅 */
export const usePostSignUpWithFile = (): UseMutationResult<
  BaseApiResponse<ResSignUp>,
  ResultError,
  MutationParams<BaseApiResponse<ResSignUp>, { user: ReqSignUp; file: File }>,
  unknown
> => {
  return useCommonMutation<
    BaseApiResponse<ResSignUp>,
    ResultError,
    { user: ReqSignUp; file: File }
  >({
    mutationKey: authKeys.SIGN_UP_WITH_FILE(),
    mutationFn: async (data) => await authApi.signUpWithFile(data),
    queryKeyToInvalidate: authKeys.SIGN_UP_WITH_FILE()
  });
};

/** Password 로그인 쿼리 훅 */
export const usePostLogin = (): UseMutationResult<
  BaseApiResponse,
  ResultError,
  MutationParams<ResLogin, ReqLogin>,
  unknown
> => {
  return useCommonMutation<ResLogin, ResultError, ReqLogin>({
    mutationKey: authKeys.LOGIN(),
    mutationFn: async (data) => await authApi.login(data),
    queryKeyToInvalidate: authKeys.LOGIN()
  });
};

/** userId 통한 비밀번호 변경 쿼리 훅 */
export const usePostChangePassword = (): UseMutationResult<
  BaseApiResponse<ResSignUp>,
  ResultError,
  MutationParams<BaseApiResponse<ResSignUp>, { userId: string; newPassword: string }>,
  unknown
> => {
  return useCommonMutation<
    BaseApiResponse<ResSignUp>,
    ResultError,
    { userId: string; newPassword: string }
  >({
    mutationKey: authKeys.AUTH_ALL,
    mutationFn: async (data) => await authApi.changePassword(data),
    queryKeyToInvalidate: authKeys.AUTH_ALL
  });
};

/** 가입 승인 관리자 페이지 조회 쿼리 훅 */
export const useGetApprovalList = ({
  data,
  options
}: {
  data: ReqApprovalList;
  options?: CommonQueryOptions<
    BaseApiResponse<unknown, UserApprovalInfo>,
    ResultError,
    UserApprovalInfo[]
  >;
}) => {
  const queryKeyFactory = (data: ReqApprovalList) => authKeys.APPROVAL(data);

  const queryFn = async (data: ReqApprovalList, pagination: Pagination) => {
    const page = {
      page: pagination.current,
      size: pagination.pageSize
    };

    return await authApi.getApprovalList({
      ...data,
      ...page
    });
  };

  return useCommonQuery<
    ReqApprovalList,
    BaseApiResponse<unknown, UserApprovalInfo>,
    ResultError,
    UserApprovalInfo[]
  >({
    data,
    options: {
      ...options,
      select: (data: BaseApiResponse<unknown, UserApprovalInfo>) => data.resultList
    },
    queryKeyFactory,
    queryFn
  });
};

/** 가입 승인 쿼리 훅 */
export const usePostApproveUsers = (): UseMutationResult<
  BaseApiResponse<string>,
  ResultError,
  MutationParams<BaseApiResponse<string>, ReqApproval>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<string>, ResultError, ReqApproval>({
    mutationKey: authKeys.APPROVE_USERS(),
    mutationFn: async (data) => await authApi.approveUsers(data),
    queryKeyToInvalidate: authKeys.APPROVE_USERS()
  });
};

/** 가입 거절 쿼리 훅 */
export const usePostRejectUsers = (): UseMutationResult<
  BaseApiResponse<string>,
  ResultError,
  MutationParams<BaseApiResponse<string>, ReqReject>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<string>, ResultError, ReqReject>({
    mutationKey: authKeys.REJECT_USERS(),
    mutationFn: async (data) => await authApi.rejectUsers(data),
    queryKeyToInvalidate: authKeys.REJECT_USERS()
  });
};

/** 아이디 변경 쿼리 훅 */
export const useChangeId = () => {
  return useCommonMutation<BaseApiResponse, ResultError, ReqChangeId>({
    mutationKey: authKeys.CHANGE_ID(),
    mutationFn: (data) => authApi.changeId(data),
    queryKeyToInvalidate: authKeys.CHANGE_ID()
  });
};
